import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AiOutlineUser,
  AiOutlineTeam,
  AiOutlineSearch,
  AiOutlineFolderAdd,
  AiOutlineUsergroupAdd,
  AiOutlineCamera,
  AiOutlineCloudUpload,
  AiOutlineScan,
  AiOutlineShareAlt,
  AiOutlineReload,
} from 'react-icons/ai';
import { apiUrl } from '../config/api';
import LocationProjectModal from './LocationProjectModal';
import NearbyJobs from './NearbyJobs';
import './Dashboard.css';

// Computes day boundaries in the browser's local timezone, converted to epoch ms — GHL wants
// epoch ms, and staff think about "today's appointments" in their own local time regardless of
// what timezone the server happens to run in.
const computeDateRange = (preset, customStart, customEnd) => {
  const startOfDay = (d) => { const x = new Date(d); x.setHours(0, 0, 0, 0); return x; };
  const endOfDay = (d) => { const x = new Date(d); x.setHours(23, 59, 59, 999); return x; };
  const now = new Date();

  switch (preset) {
    case 'today':
      return [startOfDay(now).getTime(), endOfDay(now).getTime()];
    case 'tomorrow': {
      const t = new Date(now);
      t.setDate(t.getDate() + 1);
      return [startOfDay(t).getTime(), endOfDay(t).getTime()];
    }
    case 'week': {
      const end = new Date(now);
      end.setDate(end.getDate() + 7);
      return [startOfDay(now).getTime(), endOfDay(end).getTime()];
    }
    case 'custom': {
      if (!customStart || !customEnd) return null;
      return [
        startOfDay(new Date(`${customStart}T00:00:00`)).getTime(),
        endOfDay(new Date(`${customEnd}T00:00:00`)).getTime(),
      ];
    }
    default:
      return [startOfDay(now).getTime(), endOfDay(now).getTime()];
  }
};

const formatApptTime = (ms) => {
  if (!ms) return '—';
  return new Date(ms).toLocaleString('en-US', {
    weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
  });
};

// The GoHighLevel Appointments section is hidden from Home for now. Flip to true to bring it back
// (jobs still sync into projects automatically every 30 minutes on the server).
const SHOW_APPOINTMENTS = false;

const Dashboard = ({ user, onOpenCamera }) => {
  const navigate = useNavigate();

  const [homeClients, setHomeClients] = useState([]);
  const [recentImages, setRecentImages] = useState([]);
  // 'loading' until the first response, then 'ready' — or 'error' so a failed request
  // isn't shown as "No photos yet" / "No projects".
  const [homeStatus, setHomeStatus] = useState('loading');
  const [homeSearch, setHomeSearch] = useState('');
  const [showHomeSearch, setShowHomeSearch] = useState(false);

  const [stats, setStats] = useState({
    totalClients: 0,
    totalImages: 0,
    lastUpload: null,
    totalUsers: 0,
    activeUsers: 0,
    inactiveUsers: 0,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [ghlConfigured, setGhlConfigured] = useState(true);
  const [technicians, setTechnicians] = useState([]); // GHL users appointments are assigned to
  const [appointments, setAppointments] = useState([]);
  const [apptLoading, setApptLoading] = useState(true);
  const [apptError, setApptError] = useState('');
  const [openingId, setOpeningId] = useState(null);
  const [syncingJobs, setSyncingJobs] = useState(false);
  const [syncMessage, setSyncMessage] = useState('');

  const [showLocationModal, setShowLocationModal] = useState(false);
  const [locationIntent, setLocationIntent] = useState('create');

  const [datePreset, setDatePreset] = useState('today');
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');
  const [technicianFilter, setTechnicianFilter] = useState('');

  const token = localStorage.getItem('token');
  // Bumped on every loadAppointments() call so a slower, now-stale request (e.g. one fired by an
  // intermediate keystroke while typing a custom date) can't overwrite a response to a more
  // recent one that happens to resolve first — appointments hit the live GHL API, whose latency
  // isn't guaranteed to match request order.
  const apptRequestIdRef = useRef(0);

  useEffect(() => {
    loadStats();
    if (SHOW_APPOINTMENTS) loadTechnicians();
    loadHomeData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!SHOW_APPOINTMENTS) return;
    loadAppointments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [datePreset, customStart, customEnd, technicianFilter]);

  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => setError(''), 3000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  useEffect(() => {
    if (syncMessage) {
      const timer = setTimeout(() => setSyncMessage(''), 4000);
      return () => clearTimeout(timer);
    }
  }, [syncMessage]);

  const loadStats = async () => {
    try {
      setLoading(true);
      const response = await fetch(apiUrl('/api/auth/stats'), {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json();
      if (response.ok) {
        setStats(data);
      } else {
        setError('Failed to load statistics');
      }
    } catch (err) {
      setError('Error loading statistics: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '—';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      });
    } catch {
      return '—';
    }
  };

  const loadHomeData = async () => {
    setHomeStatus('loading');
    try {
      const [clientsRes, recentRes] = await Promise.all([
        fetch(apiUrl('/api/scraper/clients'), { headers: { Authorization: `Bearer ${token}` } }),
        fetch(apiUrl('/api/scraper/images/recent?limit=10'), { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      if (!clientsRes.ok || !recentRes.ok) throw new Error(`HTTP ${clientsRes.status}/${recentRes.status}`);
      const clientsData = await clientsRes.json();
      const recentData = await recentRes.json();
      setHomeClients(clientsData.clients || []);
      setRecentImages(recentData.images || []);
      setHomeStatus('ready');
    } catch (err) {
      console.error('Failed to load home data:', err);
      setHomeStatus('error');
    }
  };

  const homeError = (what) => (
    <div className="db-empty db-empty--error">
      Couldn&apos;t load {what}.{' '}
      <button type="button" className="db-retry-btn" onClick={loadHomeData}>Retry</button>
    </div>
  );

  // With no search, the Projects row shows the 5 most recently updated projects. A search
  // goes to the server (name or address), so it reaches every project — including backup
  // ones — not just the first page loaded above. Debounced; stale responses are dropped.
  const [searchResults, setSearchResults] = useState(null);
  const homeSearchSeqRef = useRef(0);
  useEffect(() => {
    const term = homeSearch.trim();
    const seq = ++homeSearchSeqRef.current;
    if (!term) { setSearchResults(null); return undefined; }
    const timer = setTimeout(async () => {
      try {
        const params = new URLSearchParams({ search: term, limit: '20' });
        const response = await fetch(apiUrl(`/api/scraper/clients?${params.toString()}`), {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = await response.json();
        if (seq === homeSearchSeqRef.current) setSearchResults(response.ok ? (data.clients || []) : []);
      } catch (err) {
        if (seq === homeSearchSeqRef.current) setSearchResults([]);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [homeSearch, token]);

  const searching = homeSearch.trim() !== '' && searchResults === null;
  const filteredProjects = homeSearch.trim() ? (searchResults || []) : homeClients.slice(0, 5);

  const loadTechnicians = async () => {
    try {
      const response = await fetch(apiUrl('/api/ghl/technicians'), {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json();
      setGhlConfigured(Boolean(data.configured));
      setTechnicians(data.technicians || []);
    } catch (err) {
      setGhlConfigured(false);
    }
  };

  const loadAppointments = async () => {
    const requestId = ++apptRequestIdRef.current;
    const range = computeDateRange(datePreset, customStart, customEnd);
    if (!range) {
      setAppointments([]);
      setApptLoading(false);
      return;
    }
    const [startTime, endTime] = range;

    try {
      setApptLoading(true);
      setApptError('');
      const params = new URLSearchParams({ startTime, endTime });
      if (technicianFilter) params.set('technicianId', technicianFilter);

      const response = await fetch(apiUrl(`/api/ghl/appointments?${params}`), {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.message || 'Failed to load appointments');
      }
      if (requestId !== apptRequestIdRef.current) return; // a newer request has since superseded this one
      setGhlConfigured(Boolean(data.configured));
      setAppointments(data.appointments || []);
    } catch (err) {
      if (requestId !== apptRequestIdRef.current) return;
      setApptError(err.message);
      setAppointments([]);
    } finally {
      if (requestId === apptRequestIdRef.current) setApptLoading(false);
    }
  };

  const handleAppointmentClick = async (appt) => {
    if (appt.isGroupEvent || !appt.contactId || openingId) return;
    try {
      setOpeningId(appt.id);
      const response = await fetch(apiUrl(`/api/ghl/appointments/${appt.id}/open`), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ contactId: appt.contactId }),
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.message || 'Failed to open client');
      }
      navigate(`/projects/client/${data.clientId}`);
    } catch (err) {
      setApptError(err.message);
      setOpeningId(null);
    }
  };

  // Every upcoming/recent appointment already gets turned into a client automatically every 30
  // minutes in the background — this just lets staff force that pass on demand.
  const handleSyncJobs = async () => {
    setSyncingJobs(true);
    setApptError('');
    try {
      const response = await fetch(apiUrl('/api/ghl/sync-appointments'), {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.message || 'Sync failed');
      setApptError('');
      setSyncMessage(data.message);
      await loadAppointments();
    } catch (err) {
      setApptError(err.message);
    } finally {
      setSyncingJobs(false);
    }
  };

  const handleStartLocationFlow = (intent) => {
    setLocationIntent(intent);
    setShowLocationModal(true);
  };

  // 'create' lands on the resolved/created project's page; 'camera' opens the camera modal
  // preselected to it (same modal Take Photos and Upload Photos already shared before this).
  const handleLocationResolved = (client, intent) => {
    if (intent === 'create') {
      navigate(`/projects/client/${client.id}`);
    } else {
      onOpenCamera(client);
    }
  };

  return (
    <div className="db-container">
      {/* Header */}
      <div className="db-header db-home-header">
        <h1 className="db-page-title">Home</h1>
        <button
          className="db-home-search-btn"
          onClick={() => setShowHomeSearch((s) => !s)}
          aria-label="Search projects"
        >
          <AiOutlineSearch size={20} />
        </button>
      </div>

      {showHomeSearch && (
        <div className="db-home-search-row">
          <input
            type="text"
            className="db-form-input"
            placeholder="Search projects by name or address..."
            value={homeSearch}
            onChange={(e) => setHomeSearch(e.target.value)}
            autoFocus
          />
        </div>
      )}

      {/* Error Message */}
      {error && <div className="db-message db-message--error">{error}</div>}

      {/* Quick actions */}
      <div className="db-quick-actions">
        <button className="db-quick-action" onClick={() => handleStartLocationFlow('create')}>
          <span className="db-quick-action-icon"><AiOutlineFolderAdd size={20} /></span>
          <span className="db-quick-action-label">Create Project</span>
        </button>
        {(user?.isAdmin || user?.isManager) && (
          <button className="db-quick-action" onClick={() => navigate('/users')}>
            <span className="db-quick-action-icon"><AiOutlineUsergroupAdd size={20} /></span>
            <span className="db-quick-action-label">Users &amp; Groups</span>
          </button>
        )}
        <button className="db-quick-action" onClick={() => handleStartLocationFlow('camera')}>
          <span className="db-quick-action-icon"><AiOutlineCamera size={20} /></span>
          <span className="db-quick-action-label">Take Photos</span>
        </button>
        <button className="db-quick-action" onClick={() => handleStartLocationFlow('camera')}>
          <span className="db-quick-action-icon"><AiOutlineCloudUpload size={20} /></span>
          <span className="db-quick-action-label">Upload Photos</span>
        </button>
        <button className="db-quick-action" disabled>
          <span className="db-quick-action-icon"><AiOutlineScan size={20} /></span>
          <span className="db-quick-action-label">Scan Document</span>
        </button>
      </div>

      {/* Booked jobs closest to the technician */}
      <NearbyJobs token={token} />

      {/* Recent photos */}
      <div className="db-home-row">
        <div className="db-home-row-header">
          <h2 className="db-home-row-title">Photos</h2>
        </div>
        {homeStatus === 'loading' ? (
          <div className="db-photos-scroll" aria-busy="true" aria-label="Loading photos">
            {Array.from({ length: 6 }).map((_, i) => <div key={i} className="db-photo-thumb db-skeleton" />)}
          </div>
        ) : homeStatus === 'error' ? (
          homeError('photos')
        ) : recentImages.length === 0 ? (
          <div className="db-empty">No photos yet.</div>
        ) : (
          <div className="db-photos-scroll">
            {recentImages.map((img) => (
              <div
                key={img.id}
                className="db-photo-thumb"
                onClick={() => navigate(`/projects/client/${img.clientId}`)}
                title={img.clientName}
              >
                <img src={img.imageUrl} alt={img.clientName} loading="lazy" />
                {img.mediaType === 'video' && <span className="db-photo-video" aria-label="Video" />}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Projects */}
      <div className="db-home-row">
        <div className="db-home-row-header">
          <h2 className="db-home-row-title">Projects</h2>
        </div>
        {searching || (homeStatus === 'loading' && !homeSearch.trim()) ? (
          <div className="db-projects-scroll" aria-busy="true" aria-label="Loading projects">
            {Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="db-project-card db-project-card--skeleton">
                <div className="db-project-thumb db-skeleton" />
                <div className="db-project-info">
                  <span className="db-skeleton db-skeleton-line" />
                  <span className="db-skeleton db-skeleton-line db-skeleton-line--short" />
                </div>
              </div>
            ))}
          </div>
        ) : homeStatus === 'error' && !homeSearch.trim() ? (
          homeError('projects')
        ) : filteredProjects.length === 0 ? (
          <div className="db-empty">{homeSearch.trim() ? 'No projects match.' : 'No projects yet.'}</div>
        ) : (
          <div className="db-projects-scroll">
            {filteredProjects.map((c) => (
              <div key={c.id} className="db-project-card" onClick={() => navigate(`/projects/client/${c.id}`)}>
                <div className="db-project-thumb">
                  {c.thumbnailUrl ? (
                    <img src={c.thumbnailUrl} alt={c.clientName} loading="lazy" />
                  ) : (
                    <div className="db-project-thumb-placeholder">{c.clientName?.charAt(0).toUpperCase()}</div>
                  )}
                </div>
                <div className="db-project-info">
                  <span className="db-project-name">{c.clientName}</span>
                  {c.clientAddress && <span className="db-project-address">{c.clientAddress}</span>}
                </div>
                <div className="db-project-actions" onClick={(e) => e.stopPropagation()}>
                  <button className="db-project-icon-btn" aria-label="Share">
                    <AiOutlineShareAlt size={15} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Loading State */}
      {loading ? (
        <div className="db-loading">Loading statistics...</div>
      ) : (
        <>
          {/* Admin Stats Section */}
          {user?.isAdmin && (
            <>
              <div className="db-section-divider">
                <h2>System Statistics</h2>
              </div>

              <div className="db-stats-row">
                <div className="db-stat-card db-stat-card--highlight">
                  <div className="db-stat-icon">
                    <AiOutlineTeam size={18} />
                  </div>
                  <span className="db-stat-label">Total Users</span>
                  <span className="db-stat-value">{stats.totalUsers}</span>
                </div>

                <div className="db-stat-card db-stat-card--highlight">
                  <div className="db-stat-icon">
                    <AiOutlineUser size={18} />
                  </div>
                  <span className="db-stat-label">Active Users</span>
                  <span className="db-stat-value">{stats.activeUsers}</span>
                </div>

                <div className="db-stat-card db-stat-card--highlight">
                  <div className="db-stat-icon">
                    <AiOutlineUser size={18} />
                  </div>
                  <span className="db-stat-label">Inactive Users</span>
                  <span className="db-stat-value">{stats.inactiveUsers}</span>
                </div>
              </div>

              <div className="db-stats-row">
                <div className="db-stat-card db-stat-card--highlight">
                  <div className="db-stat-icon">
                    <AiOutlineTeam size={18} />
                  </div>
                  <span className="db-stat-label">Total Clients</span>
                  <span className="db-stat-value">{stats.totalClients}</span>
                </div>

                <div className="db-stat-card db-stat-card--highlight">
                  <div className="db-stat-icon">
                    <AiOutlineUser size={18} />
                  </div>
                  <span className="db-stat-label">Total Images</span>
                  <span className="db-stat-value">{stats.totalImages}</span>
                </div>

                <div className="db-stat-card db-stat-card--highlight">
                  <div className="db-stat-icon">
                    <AiOutlineUser size={18} />
                  </div>
                  <span className="db-stat-label">Last Upload</span>
                  <span className="db-stat-value">{formatDate(stats.lastUpload)}</span>
                </div>
              </div>

              <div className="db-admin-note">
                <p>Manage users from the <strong>User Management</strong> section in the sidebar.</p>
              </div>
            </>
          )}

          {SHOW_APPOINTMENTS && (
          <>
          <div className="db-section-divider db-section-divider--with-action">
            <h2>Appointments</h2>
            {ghlConfigured && (
              <button
                type="button"
                className="db-sync-jobs-btn"
                onClick={handleSyncJobs}
                disabled={syncingJobs}
                title="Turn every upcoming/recent appointment into a client (this also runs automatically every 30 minutes)"
              >
                <AiOutlineReload size={14} className={syncingJobs ? 'db-spin' : ''} />
                {syncingJobs ? 'Syncing…' : 'Sync Jobs'}
              </button>
            )}
          </div>

          {syncMessage && <div className="db-message db-message--success">{syncMessage}</div>}

          {!ghlConfigured ? (
            <div className="db-empty db-appt-empty">
              GHL is not connected yet. Add <code>GHL_API_TOKEN</code> to the server environment to enable appointments.
            </div>
          ) : (
            <div className="db-card db-appt-card">
              <div className="db-appt-filters">
                <div className="db-form-group">
                  <label className="db-form-label">Date Range</label>
                  <select
                    className="db-form-input db-appt-select"
                    value={datePreset}
                    onChange={(e) => setDatePreset(e.target.value)}
                  >
                    <option value="today">Today</option>
                    <option value="tomorrow">Tomorrow</option>
                    <option value="week">Next 7 Days</option>
                    <option value="custom">Custom Range</option>
                  </select>
                </div>

                {datePreset === 'custom' && (
                  <>
                    <div className="db-form-group">
                      <label className="db-form-label">Start</label>
                      <input
                        type="date"
                        className="db-form-input"
                        value={customStart}
                        onChange={(e) => setCustomStart(e.target.value)}
                      />
                    </div>
                    <div className="db-form-group">
                      <label className="db-form-label">End</label>
                      <input
                        type="date"
                        className="db-form-input"
                        value={customEnd}
                        onChange={(e) => setCustomEnd(e.target.value)}
                      />
                    </div>
                  </>
                )}

                <div className="db-form-group">
                  <label className="db-form-label">Technician</label>
                  <select
                    className="db-form-input db-appt-select"
                    value={technicianFilter}
                    onChange={(e) => setTechnicianFilter(e.target.value)}
                  >
                    <option value="">All Technicians</option>
                    {technicians.map((t) => (
                      <option key={t.id} value={t.id}>{t.name}</option>
                    ))}
                  </select>
                </div>
              </div>

              {apptError && <div className="db-message db-message--error">{apptError}</div>}

              {apptLoading ? (
                <div className="db-loading">Loading appointments...</div>
              ) : appointments.length === 0 ? (
                <div className="db-empty">No appointments in this range.</div>
              ) : (
                <div className="db-appt-list">
                  {appointments.map((appt) => (
                    <div
                      key={appt.id}
                      className={`db-appt-row ${appt.isGroupEvent ? 'db-appt-row--disabled' : ''} ${openingId === appt.id ? 'db-appt-row--loading' : ''}`}
                      onClick={() => handleAppointmentClick(appt)}
                    >
                      <div className="db-appt-row-main">
                        <span className="db-appt-name">
                          {appt.customerName}{appt.address ? ` - ${appt.address}` : ''}
                        </span>
                        <span className="db-appt-time">{formatApptTime(appt.startTime)}</span>
                      </div>
                      <div className="db-appt-row-meta">
                        <span className="db-appt-calendar" title={`Calendar: ${appt.calendarName}`}>
                          <AiOutlineUser size={13} /> {appt.technicianName || 'Unassigned'}
                        </span>
                        <span className={`db-status-badge db-status-badge--${appt.status}`}>{appt.status}</span>
                        {appt.existingClientId && <span className="db-appt-linked">Linked</span>}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
          </>
          )}
        </>
      )}

      <LocationProjectModal
        isOpen={showLocationModal}
        onClose={() => setShowLocationModal(false)}
        intent={locationIntent}
        clients={homeClients}
        token={token}
        onResolved={handleLocationResolved}
      />
    </div>
  );
};

export default Dashboard;
